export type ColorVector = [number, number, number];

export type ColorMatrix = [ColorVector, ColorVector, ColorVector];

export type ParsedColor = {
    rgb: ColorVector;
    isRgba: boolean;
    alpha: number;
};

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const RGBA_PATTERN = /^rgba?\(([^)]*)\)$/i;

const LMS_FROM_LINEAR: ColorMatrix = [
    [
        0.4122214708,
        0.5363325363,
        0.0514459929
    ],
    [
        0.2119034982,
        0.6806995451,
        0.1073969566
    ],
    [
        0.0883024619,
        0.2817188376,
        0.6299787005
    ]
];

const OKLAB_FROM_LMS: ColorMatrix = [
    [
        0.2104542553,
        0.793617785,
        -0.0040720468
    ],
    [
        1.9779984951,
        -2.428592205,
        0.4505937099
    ],
    [
        0.0259040371,
        0.7827717662,
        -0.808675766
    ]
];

const LMS_FROM_OKLAB: ColorMatrix = [
    [
        1,
        0.3963377774,
        0.2158037573
    ],
    [
        1,
        -0.1055613458,
        -0.0638541728
    ],
    [
        1,
        -0.0894841775,
        -1.291485548
    ]
];

const LINEAR_FROM_LMS: ColorMatrix = [
    [
        4.0767416621,
        -3.3077115913,
        0.2309699292
    ],
    [
        -1.2684380046,
        2.6097574011,
        -0.3413193965
    ],
    [
        -0.0041960863,
        -0.7034186147,
        1.707614701
    ]
];

const LUMINANCE_WEIGHTS: ColorVector = [
    0.2126,
    0.7152,
    0.0722
];

const APCA_WEIGHTS: ColorVector = [
    0.2126729,
    0.7151522,
    0.072175
];

const APCA_BLACK_THRESHOLD = 0.022;

const APCA_BLACK_CLAMP = 1.414;

const APCA_DELTA_Y_MIN = 0.0005;

const APCA_NORMAL_BACKGROUND = 0.56;

const APCA_REVERSE_BACKGROUND = 0.65;

const APCA_NORMAL_TEXT = 0.57;

const APCA_REVERSE_TEXT = 0.62;

const APCA_LOW_OFFSET = 0.027;

const APCA_LOW_CLIP = 0.1;

const APCA_TRC = 2.4;

const APCA_SCALE = 1.14;

const GAMUT_TOLERANCE = 0.0001;

const MAX_CHROMA_TOLERANCE = 0.001;

const MAX_CHROMA_LIMIT = 0.4;

const MAX_CHROMA_STEPS = 20;

const MAX_CHROMA_SCAN_STEP = 0.005;

const ALPHA_PRECISION = 1000;

const CHANNEL_MAX = 255;

const getDotProduct = (row: ColorVector, vector: ColorVector) => {
    return row[0] * vector[0] + row[1] * vector[1] + row[2] * vector[2];
};

const multiplyMatrix = (matrix: ColorMatrix, vector: ColorVector): ColorVector => {
    return [
        getDotProduct(matrix[0], vector),
        getDotProduct(matrix[1], vector),
        getDotProduct(matrix[2], vector)
    ];
};

const mapVector = (vector: ColorVector, mapper: (value: number) => number): ColorVector => {
    return [
        mapper(vector[0]),
        mapper(vector[1]),
        mapper(vector[2])
    ];
};

const clampUnit = (value: number) => {
    return Math.min(Math.max(value, 0), 1);
};

export const normalizeHue = (hue: number) => {
    return (hue % 360 + 360) % 360;
};

export const getHueDifference = (from: number, to: number) => {
    const difference = normalizeHue(to - from);

    return difference > 180 ? difference - 360 : difference;
};

export const toLinearChannel = (channel: number) => {
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

export const toGammaChannel = (channel: number) => {
    return channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055;
};

export const parseColor = (value: string): ParsedColor | null => {
    const hexMatch = HEX_PATTERN.exec(value.trim());

    if(hexMatch && hexMatch[1]) {
        const digits = hexMatch[1].length === 3 ? hexMatch[1].split("").map((digit) => `${digit}${digit}`).join("") : hexMatch[1];

        return {
            rgb: [
                parseInt(digits.slice(0, 2), 16) / CHANNEL_MAX,
                parseInt(digits.slice(2, 4), 16) / CHANNEL_MAX,
                parseInt(digits.slice(4, 6), 16) / CHANNEL_MAX
            ],
            isRgba: false,
            alpha: 1
        };
    }

    const rgbaMatch = RGBA_PATTERN.exec(value.trim());

    if(!rgbaMatch || !rgbaMatch[1]) {
        return null;
    }

    const channels = rgbaMatch[1].split(/[\s,/]+/).filter((channel) => channel.length > 0).map(Number);

    if(channels.length < 3 || channels.some((channel) => Number.isNaN(channel))) {
        return null;
    }

    return {
        rgb: [
            clampUnit((channels[0] ?? 0) / CHANNEL_MAX),
            clampUnit((channels[1] ?? 0) / CHANNEL_MAX),
            clampUnit((channels[2] ?? 0) / CHANNEL_MAX)
        ],
        alpha: clampUnit(channels[3] ?? 1),
        isRgba: true
    };
};

export const toChannels = (rgb: ColorVector): ColorVector => {
    return mapVector(rgb, (channel) => Math.round(clampUnit(channel) * CHANNEL_MAX));
};

export const formatHex = (rgb: ColorVector) => {
    return `#${toChannels(rgb).map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
};

export const roundAlpha = (alpha: number) => {
    return Math.round(clampUnit(alpha) * ALPHA_PRECISION) / ALPHA_PRECISION;
};

export const formatRgba = ({
    alpha,
    rgb
}: {
    rgb: ColorVector;
    alpha: number;
}) => {
    return `rgba(${toChannels(rgb).join(",")},${roundAlpha(alpha)})`;
};

export const rgbToLinear = (rgb: ColorVector): ColorVector => {
    return mapVector(rgb, toLinearChannel);
};

export const linearToRgb = (linear: ColorVector): ColorVector => {
    return mapVector(linear, (channel) => toGammaChannel(clampUnit(channel)));
};

export const linearToOklab = (linear: ColorVector): ColorVector => {
    return multiplyMatrix(OKLAB_FROM_LMS, mapVector(multiplyMatrix(LMS_FROM_LINEAR, linear), Math.cbrt));
};

export const oklabToLinear = (oklab: ColorVector): ColorVector => {
    return multiplyMatrix(LINEAR_FROM_LMS, mapVector(multiplyMatrix(LMS_FROM_OKLAB, oklab), (value) => value ** 3));
};

export const rgbToOklab = (rgb: ColorVector): ColorVector => {
    return linearToOklab(rgbToLinear(rgb));
};

export const oklabToOklch = (oklab: ColorVector): ColorVector => {
    return [
        oklab[0],
        Math.hypot(oklab[1], oklab[2]),
        normalizeHue(Math.atan2(oklab[2], oklab[1]) * 180 / Math.PI)
    ];
};

export const oklchToOklab = (oklch: ColorVector): ColorVector => {
    const angle = oklch[2] * Math.PI / 180;

    return [
        oklch[0],
        oklch[1] * Math.cos(angle),
        oklch[1] * Math.sin(angle)
    ];
};

export const rgbToOklch = (rgb: ColorVector): ColorVector => {
    return oklabToOklch(rgbToOklab(rgb));
};

export const oklchToLinear = (oklch: ColorVector): ColorVector => {
    return oklabToLinear(oklchToOklab(oklch));
};

export const oklchToRgb = (oklch: ColorVector): ColorVector => {
    return linearToRgb(oklchToLinear(oklch));
};

export const isLinearInGamut = (linear: ColorVector) => {
    return linear.every((channel) => channel >= -GAMUT_TOLERANCE && channel <= 1 + GAMUT_TOLERANCE);
};

export const getMaxChroma = (lightness: number, hue: number) => {
    if(lightness <= 0 || lightness >= 1) {
        return 0;
    }

    const tolerance = Math.min(MAX_CHROMA_TOLERANCE, lightness ** 3 / 10);

    const isInside = (chroma: number) => {
        return oklchToLinear([
            lightness,
            chroma,
            hue
        ]).every((channel) => channel >= -tolerance && channel <= 1 + tolerance);
    };

    let low = 0;

    let high = MAX_CHROMA_LIMIT;

    for(let chroma = MAX_CHROMA_SCAN_STEP; chroma <= MAX_CHROMA_LIMIT; chroma += MAX_CHROMA_SCAN_STEP) {
        if(!isInside(chroma)) {
            high = chroma;

            break;
        }

        low = chroma;
    }

    if(high === MAX_CHROMA_LIMIT && isInside(MAX_CHROMA_LIMIT)) {
        return MAX_CHROMA_LIMIT;
    }

    for(let step = 0; step < MAX_CHROMA_STEPS; step++) {
        const middle = (low + high) / 2;

        if(isInside(middle)) {
            low = middle;
        } else {
            high = middle;
        }
    }

    return low;
};

export const getRelativeChroma = (oklch: ColorVector) => {
    const maxChroma = getMaxChroma(oklch[0], oklch[2]);

    return maxChroma > 0 ? Math.min(oklch[1] / maxChroma, 1) : 0;
};

export const getLuminance = (rgb: ColorVector) => {
    return getDotProduct(LUMINANCE_WEIGHTS, rgbToLinear(rgb));
};

export const getContrastFromLuminance = (first: number, second: number) => {
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
};

export const getContrastRatio = (first: ColorVector, second: ColorVector) => {
    return getContrastFromLuminance(getLuminance(first), getLuminance(second));
};

const getApcaLuminance = (rgb: ColorVector) => {
    const luminance = getDotProduct(APCA_WEIGHTS, mapVector(toChannels(rgb), (channel) => (channel / CHANNEL_MAX) ** APCA_TRC));

    return luminance < APCA_BLACK_THRESHOLD ? luminance + (APCA_BLACK_THRESHOLD - luminance) ** APCA_BLACK_CLAMP : luminance;
};

export const getApcaContrast = ({
    background,
    text
}: {
    background: ColorVector;
    text: ColorVector;
}) => {
    const backgroundLuminance = getApcaLuminance(background);

    const textLuminance = getApcaLuminance(text);

    if(Math.abs(backgroundLuminance - textLuminance) < APCA_DELTA_Y_MIN) {
        return 0;
    }

    if(backgroundLuminance > textLuminance) {
        const contrast = (backgroundLuminance ** APCA_NORMAL_BACKGROUND - textLuminance ** APCA_NORMAL_TEXT) * APCA_SCALE;

        return contrast < APCA_LOW_CLIP ? 0 : (contrast - APCA_LOW_OFFSET) * 100;
    }

    const contrast = (backgroundLuminance ** APCA_REVERSE_BACKGROUND - textLuminance ** APCA_REVERSE_TEXT) * APCA_SCALE;

    return contrast > -APCA_LOW_CLIP ? 0 : (contrast + APCA_LOW_OFFSET) * 100;
};

export const compositeOver = ({
    background,
    foreground,
    alpha
}: {
    background: ColorVector;
    foreground: ColorVector;
    alpha: number;
}): ColorVector => {
    return [
        foreground[0] * alpha + background[0] * (1 - alpha),
        foreground[1] * alpha + background[1] * (1 - alpha),
        foreground[2] * alpha + background[2] * (1 - alpha)
    ];
};

export type PickerColor = {
    rgb: ColorVector;
    alpha: number;
};

export type OklchColor = {
    l: number;
    c: number;
    h: number;
};

export type OklchAlphaColor = {
    alpha: number;
    c: number;
    h: number;
    l: number;
};

export type ContrastSnapInput = {
    isApcaGated: boolean;
    surface: ColorVector;
    target?: number;
    alpha: number;
    c: number;
    h: number;
    l: number;
};

export type ColorName = {
    modifier: "colorName-light" | "colorName-dark" | null;
    key: string;
};

export type ContrastGuide = {
    points: Array<[number, number]>;
    top: [number, number] | null;
};

const ANY_HEX_PATTERN = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const FUNCTION_PATTERN = /^(rgba?|oklch)\(([^)]*)\)$/i;

const NUMBER_PATTERN = /^[+-]?(\d+\.?\d*|\.\d+)(%|deg)?$/i;

const HUE_NAMES: Array<{
    limit: number;
    name: string;
}> = [
    {
        name: "pink",
        limit: 20
    },
    {
        name: "red",
        limit: 45
    },
    {
        name: "orange",
        limit: 70
    },
    {
        name: "yellow",
        limit: 105
    },
    {
        name: "green",
        limit: 160
    },
    {
        name: "teal",
        limit: 200
    },
    {
        name: "cyan",
        limit: 245
    },
    {
        name: "blue",
        limit: 290
    },
    {
        name: "purple",
        limit: 330
    },
    {
        name: "pink",
        limit: 360
    }
];

const NEUTRAL_CHROMA = 0.03;

const WHITE_LIGHTNESS = 0.95;

const BLACK_LIGHTNESS = 0.12;

const LIGHT_NAME_LIGHTNESS = 0.8;

const DARK_NAME_LIGHTNESS = 0.45;

const NEUTRAL_HUE_CHROMA = 0.0001;

const CUSP_COARSE_STEP = 0.01;

const CUSP_FINE_STEP = 0.001;

const CUSP_HUE_PRECISION = 100;

const OKLCH_CHROMA_PERCENT = 0.4;

const GUIDE_BISECT_STEPS = 18;

const GUIDE_EDGE_STEPS = 10;

const GUIDE_EDGE_TOLERANCE = 0.0001;

const GUIDE_TABLE_SIZE = 128;

const SNAP_BISECT_STEPS = 24;

const APCA_TEXT_LEVEL = 60;

const DEFAULT_CONTRAST_TARGET = 4.5;

const DEFAULT_GUIDE_STEP = 4;

const ALPHA_CHANNEL_MAX = 255;

const cuspCache = new Map<number, number>();

const parseNumberToken = (token: string, percentScale: number) => {
    if(!NUMBER_PATTERN.test(token)) {
        return null;
    }

    const value = parseFloat(token);

    return token.endsWith("%") ? value / 100 * percentScale : value;
};

const parseAlphaToken = (token: string | undefined) => {
    if(token === undefined) {
        return 1;
    }

    if(!NUMBER_PATTERN.test(token) || token.toLowerCase().endsWith("deg")) {
        return null;
    }

    return clampUnit(parseNumberToken(token, 1) ?? 1);
};

const splitFunctionArguments = (inner: string) => {
    const content = inner.trim();

    const slashParts = content.split("/");

    if(slashParts.length > 2) {
        return null;
    }

    const left = (slashParts[0] ?? "").trim().split(/\s*,\s*|\s+/).filter((part) => part.length > 0);

    if(slashParts.length === 2) {
        const right = (slashParts[1] ?? "").trim();

        if(left.length !== 3 || right.length === 0 || /[\s,]/.test(right) || content.includes(",")) {
            return null;
        }

        return [
            ...left,
            right
        ];
    }

    return left.length === 3 || left.length === 4 ? left : null;
};

const parseHexString = (value: string): PickerColor | null => {
    const match = ANY_HEX_PATTERN.exec(value);

    if(!match || !match[1]) {
        return null;
    }

    const digits = match[1].length <= 4 ? match[1].split("").map((digit) => `${digit}${digit}`).join("") : match[1];

    return {
        alpha: digits.length === 8 ? parseInt(digits.slice(6, 8), 16) / ALPHA_CHANNEL_MAX : 1,
        rgb: [
            parseInt(digits.slice(0, 2), 16) / CHANNEL_MAX,
            parseInt(digits.slice(2, 4), 16) / CHANNEL_MAX,
            parseInt(digits.slice(4, 6), 16) / CHANNEL_MAX
        ]
    };
};

const parseRgbArguments = (parts: string[]): PickerColor | null => {
    const channels = parts.slice(0, 3).map((part) => parseNumberToken(part, CHANNEL_MAX));

    const alpha = parseAlphaToken(parts[3]);

    if(channels.some((channel) => channel === null) || alpha === null || parts.slice(0, 3).some((part) => part.toLowerCase().endsWith("deg"))) {
        return null;
    }

    return {
        rgb: [
            clampUnit((channels[0] ?? 0) / CHANNEL_MAX),
            clampUnit((channels[1] ?? 0) / CHANNEL_MAX),
            clampUnit((channels[2] ?? 0) / CHANNEL_MAX)
        ],
        alpha
    };
};

const parseOklchArguments = (parts: string[]): PickerColor | null => {
    const lightness = parseNumberToken(parts[0] ?? "", 1);

    const chroma = parseNumberToken(parts[1] ?? "", OKLCH_CHROMA_PERCENT);

    const hue = parseNumberToken(parts[2] ?? "", 360);

    const alpha = parseAlphaToken(parts[3]);

    if(lightness === null || chroma === null || hue === null || alpha === null || (parts[0] ?? "").toLowerCase().endsWith("deg") || (parts[1] ?? "").toLowerCase().endsWith("deg") || (parts[2] ?? "").endsWith("%")) {
        return null;
    }

    return {
        rgb: getClippedRgb(clampUnit(lightness), Math.max(chroma, 0), normalizeHue(hue)),
        alpha
    };
};

const getClippedRgb = (lightness: number, chroma: number, hue: number): ColorVector => {
    const linear = oklchToLinear([
        lightness,
        chroma,
        hue
    ]);

    if(isLinearInGamut(linear)) {
        return linearToRgb(linear);
    }

    return oklchToRgb([
        lightness,
        Math.min(chroma, getMaxChroma(lightness, hue)),
        hue
    ]);
};

const getMeasuredContrast = ({
    isApcaGated,
    lightness,
    surface,
    chroma,
    alpha,
    hue
}: {
    isApcaGated: boolean;
    surface: ColorVector;
    lightness: number;
    chroma: number;
    alpha: number;
    hue: number;
}) => {
    const rgb = mapVector(toChannels(getClippedRgb(lightness, chroma, hue)), (channel) => channel / CHANNEL_MAX);

    const foreground = compositeOver({
        background: surface,
        foreground: rgb,
        alpha
    });

    const ratio = getContrastRatio(foreground, surface);

    const level = Math.abs(getApcaContrast({
        background: surface,
        text: foreground
    }));

    return {
        level: isApcaGated ? level : Infinity,
        ratio
    };
};

export const parseColorString = (input: string): PickerColor | null => {
    const value = input.trim();

    if(value.length === 0) {
        return null;
    }

    const functionMatch = FUNCTION_PATTERN.exec(value);

    if(!functionMatch) {
        return parseHexString(value);
    }

    const parts = splitFunctionArguments(functionMatch[2] ?? "");

    if(!parts) {
        return null;
    }

    return (functionMatch[1] ?? "").toLowerCase() === "oklch" ? parseOklchArguments(parts) : parseRgbArguments(parts);
};

export const formatColorHex = ({
    isAlphaEnabled,
    alpha,
    rgb
}: {
    isAlphaEnabled: boolean;
    rgb: ColorVector;
    alpha: number;
}) => {
    const hex = formatHex(rgb);

    if(!isAlphaEnabled || alpha >= 1) {
        return hex;
    }

    return `${hex}${Math.round(clampUnit(alpha) * ALPHA_CHANNEL_MAX).toString(16).padStart(2, "0")}`;
};

export const oklchToHex = (lightness: number, chroma: number, hue: number) => {
    return formatHex(getClippedRgb(lightness, chroma, hue));
};

export const hexToOklch = (hex: string): OklchAlphaColor | null => {
    const parsed = parseHexString(hex.trim());

    if(!parsed) {
        return null;
    }

    const [
        l,
        c,
        h
    ] = rgbToOklch(parsed.rgb);

    return {
        h: (c as number) < NEUTRAL_HUE_CHROMA ? 0 : h as number,
        alpha: parsed.alpha,
        c: c as number,
        l: l as number
    };
};

export const getCuspChroma = (hue: number) => {
    const key = Math.round(normalizeHue(hue) * CUSP_HUE_PRECISION) / CUSP_HUE_PRECISION;

    const cached = cuspCache.get(key);

    if(cached !== undefined) {
        return cached;
    }

    let best = 0;

    let bestLightness = 0.5;

    for(let lightness = CUSP_COARSE_STEP; lightness < 1; lightness += CUSP_COARSE_STEP) {
        const chroma = getMaxChroma(lightness, key);

        if(chroma > best) {
            best = chroma;

            bestLightness = lightness;
        }
    }

    for(let lightness = bestLightness - CUSP_COARSE_STEP; lightness <= bestLightness + CUSP_COARSE_STEP; lightness += CUSP_FINE_STEP) {
        best = Math.max(best, getMaxChroma(lightness, key));
    }

    cuspCache.set(key, best);

    return best;
};

export const getColorName = ({
    c,
    h,
    l
}: OklchColor): ColorName => {
    if(l > WHITE_LIGHTNESS) {
        return {
            key: "colorName-white",
            modifier: null
        };
    }

    if(l < BLACK_LIGHTNESS) {
        return {
            key: "colorName-black",
            modifier: null
        };
    }

    const hueName = c < NEUTRAL_CHROMA ? "gray" : HUE_NAMES.find((entry) => normalizeHue(h) < entry.limit)?.name ?? "pink";

    let modifier: ColorName["modifier"] = null;

    if(l > LIGHT_NAME_LIGHTNESS) {
        modifier = "colorName-light";
    } else if(l < DARK_NAME_LIGHTNESS) {
        modifier = "colorName-dark";
    }

    return {
        key: `colorName-${hueName}`,
        modifier
    };
};

export const getContrastSnapLightness = ({
    target = DEFAULT_CONTRAST_TARGET,
    isApcaGated,
    surface,
    alpha,
    c,
    h,
    l
}: ContrastSnapInput) => {
    const isGood = (lightness: number) => {
        const measured = getMeasuredContrast({
            isApcaGated,
            chroma: c,
            lightness,
            surface,
            alpha,
            hue: h
        });

        return measured.ratio >= target && measured.level >= APCA_TEXT_LEVEL;
    };

    if(isGood(l)) {
        return null;
    }

    const found: number[] = [];

    if(isGood(0)) {
        let low = 0;

        let high = l;

        for(let step = 0; step < SNAP_BISECT_STEPS; step++) {
            const middle = (low + high) / 2;

            if(isGood(middle)) {
                low = middle;
            } else {
                high = middle;
            }
        }

        found.push(low);
    }

    if(isGood(1)) {
        let low = l;

        let high = 1;

        for(let step = 0; step < SNAP_BISECT_STEPS; step++) {
            const middle = (low + high) / 2;

            if(isGood(middle)) {
                high = middle;
            } else {
                low = middle;
            }
        }

        found.push(high);
    }

    if(found.length === 0) {
        return null;
    }

    return found.sort((first, second) => Math.abs(first - l) - Math.abs(second - l))[0] ?? null;
};

export const getContrastGuidePoints = ({
    step = DEFAULT_GUIDE_STEP,
    isChromaRelative = false,
    axisMax,
    surface,
    height,
    target,
    alpha,
    width,
    hue
}: {
    isChromaRelative?: boolean;
    surface: ColorVector;
    axisMax: number;
    target: number;
    height: number;
    alpha: number;
    width: number;
    step?: number;
    hue: number;
}): ContrastGuide => {
    const chromaTable = isChromaRelative ? Array.from({
        length: GUIDE_TABLE_SIZE + 1
    }, (_item, index) => getMaxChroma(index / GUIDE_TABLE_SIZE, hue)) : [];

    const getChroma = (fraction: number, lightness: number) => {
        if(!isChromaRelative) {
            return fraction * axisMax;
        }

        const position = Math.min(Math.max(lightness, 0), 1) * GUIDE_TABLE_SIZE;

        const index = Math.min(Math.floor(position), GUIDE_TABLE_SIZE - 1);

        const low = chromaTable[index] ?? 0;

        const high = chromaTable[index + 1] ?? low;

        return fraction * (low + (high - low) * (position - index));
    };

    const getDifference = (lightness: number, fraction: number) => {
        return getMeasuredContrast({
            chroma: getChroma(fraction, lightness),
            isApcaGated: false,
            lightness,
            surface,
            alpha,
            hue
        }).ratio - target;
    };

    const getCrossing = (fraction: number) => {
        const atBlack = getDifference(0, fraction);

        if(atBlack * getDifference(1, fraction) > 0) {
            return null;
        }

        let low = 0;

        let high = 1;

        for(let index = 0; index < GUIDE_BISECT_STEPS; index++) {
            const middle = (low + high) / 2;

            if(getDifference(middle, fraction) * atBlack > 0) {
                low = middle;
            } else {
                high = middle;
            }
        }

        return (low + high) / 2;
    };

    const getInside = (fraction: number) => {
        const lightness = getCrossing(fraction);

        if(lightness === null || isChromaRelative) {
            return lightness;
        }

        return fraction * axisMax <= getMaxChroma(lightness, hue) + GUIDE_EDGE_TOLERANCE ? lightness : null;
    };

    const points: Array<[number, number]> = [];

    let lastFraction: number | null = null;

    for(let x = 0; x <= width; x += step) {
        const fraction = x / width;

        const lightness = getInside(fraction);

        if(lightness === null) {
            if(lastFraction !== null) {
                let low = lastFraction;

                let high = fraction;

                for(let index = 0; index < GUIDE_EDGE_STEPS; index++) {
                    const middle = (low + high) / 2;

                    if(getInside(middle) !== null) {
                        low = middle;
                    } else {
                        high = middle;
                    }
                }

                points.push([
                    low * width,
                    (1 - (getInside(low) ?? 0)) * height
                ]);
            }

            break;
        }

        points.push([
            x,
            (1 - lightness) * height
        ]);

        lastFraction = fraction;
    }

    return {
        top: points.reduce<[number, number] | null>((top, point) => !top || point[1] < top[1] ? point : top, null),
        points
    };
};
